import { Box, Button, CircularProgress, Stack, TextField } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useFirstRender } from 'utils/hooks/useFirstRender'
import ToastAlert from '../../../../core/Layouts/ToastAlert'
import SideDialog from '../../components/SideDialog'
import useGroupQuery from '../../hooks/useGroupQuery'
import useView from '../../hooks/useView'
import JobSummaryForm from './JobSummaryForm'
import { isEqual } from 'lodash'

function JobSummarySettingsDialog({
	defaultTileWidth,
	tileElementWidth,
	settings,
	isSubmitting,
	open,
	tile,
	showStable,
	onClose,
	onSubmit,
	dashboardKeyList,
	onResizeTileWidth,
	menuKeyInUrl,
}) {
	const { environment } = useSelector((state) => state)
	const [t] = useTranslation('common')
	const isFirstRender = useFirstRender()

	const { groups } = useGroupQuery()

	const selectedWebGroupKey = useMemo(() => {
		if (!Array.isArray(groups) || !groups?.length) return ''
		return groups?.find((group) => group?.menuKey != null && group?.menuKey === menuKeyInUrl)?.key
	}, [groups, menuKeyInUrl])

	const [alertInfo, setAlertInfo] = useState({
		open: false,
		text: '',
		type: 'success',
	})
	const dialogWidth = tile?.w ?? 6
	const STABLE_WIDTH = 6

	const [filterData, setFilterData] = useState(settings || {})

	useEffect(() => {
		if (isEqual(filterData, settings)) return
		setFilterData(settings)
	}, [settings])

	const { viewLoading, views, reloadViews } = useView({ config: { open }, selectedWebGroupKey })

	const handleSettingsChange = (name, value) => {
		setFilterData((prev) => ({
			...prev,
			[name]: value,
		}))
	}

	const handleCloseDialog = () => {
		setFilterData(settings)
		onClose()
	}

	const handleSubmit = () => {
		if (filterData?.columnsChecked?.length === 0) {
			setAlertInfo((prev) => ({
				...prev,
				open: true,
				type: 'error',
				text: t('common:dis.pleaseFillInformation'),
			}))
			return
		}

		onSubmit?.({
			columnsChecked: filterData?.columnsChecked,
			selectedNewJobForm: filterData?.selectedNewJobForm,
			selectedJobForm: filterData?.selectedJobForm,
			selectedCustomerForm: filterData?.selectedCustomerForm,
			selectedJobActivityForm: filterData?.selectedJobActivityForm,
			summaryWidgets: filterData?.summaryWidgets,
			filterField: filterData?.filterField,
			jobIdField: filterData?.jobIdField,
			jobIdFieldCaption: filterData?.jobIdFieldCaption,
			primaryKeyList: filterData?.primaryKeyList,
			statusField: filterData?.statusField,
			progressBarField: filterData?.progressBarField,
			showProgressBar: filterData?.showProgressBar,
			showPercentage: filterData?.showPercentage,
			selectedNewTaskForm: filterData?.selectedNewTaskForm,
			taskSummaryWidgets: filterData?.taskSummaryWidgets,
			taskStatusField: filterData?.taskStatusField,
			modalDashboardSetting: filterData?.modalDashboardSetting,
		})
	}

	return (
		<>
			<ToastAlert alertInfo={alertInfo} setAlertInfo={setAlertInfo} />

			<SideDialog
				defaultTileWidth={defaultTileWidth}
				open={open}
				onClose={onClose}
				width={showStable ? STABLE_WIDTH : dialogWidth}
				tileElementWidth={tileElementWidth}
				onResizeWidth={onResizeTileWidth}
			>
				<Stack
					sx={{
						'& .MuiInputBase-input': {
							fontSize: '0.9rem !important',
						},
						'& .MuiCheckbox-root': {
							p: 0,
						},
					}}
				>
					<Box sx={{ px: 1 }}>
						<TextField
							fullWidth
							id="label-basic"
							variant="standard"
							value={tile.i}
							disabled={true}
							size="small"
						/>
					</Box>
					<Box sx={{ px: 1, mt: 2 }}>
						<JobSummaryForm
							isSubmitting={isSubmitting}
							filterData={filterData}
							onFilterDataChange={handleSettingsChange}
						/>
					</Box>
					<Stack direction="row" justifyContent="flex-end" mt={2}>
						<Stack spacing={2} direction="row">
							<Button
								size="small"
								variant="contained"
								onClick={handleSubmit}
								disabled={isSubmitting || viewLoading}
								startIcon={
									isSubmitting || viewLoading ? (
										<CircularProgress color="inherit" size="1em" />
									) : null
								}
							>
								{t('common:misc.ok')}
							</Button>
							<Button size="small" variant="text" onClick={handleCloseDialog}>
								{t('common:misc.cancel')}
							</Button>
						</Stack>
					</Stack>
				</Stack>
			</SideDialog>
		</>
	)
}

export default JobSummarySettingsDialog
